/**
 * Resume-scan banner.
 *
 * Renders only when /api/user/state reported an in-flight scan
 * (scan_sessions row with status='in_progress'). The trainer can resume
 * (open the live scan / picker again) or dismiss (PATCH the session to
 * status='cancelled' server-side so it never resurfaces).
 *
 * Wired into the trainer profile (Profile.tsx) because that is the post-
 * login landing page; trainers see this above the fold the moment they
 * return to the app.
 */
import { Radio, X } from "lucide-react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { useSession } from "@/components/SessionProvider";
import { apiRequestRaw } from "@/lib/queryClient";
import { SwipeDismissible, type SwipeDismissibleHandle } from "@/components/SwipeDismissible";

interface ResumeScanBannerProps {
  /** Opens the same flow the original scan came from. */
  onResume?: (source: string) => void;
}

const SOURCE_LABEL: Record<string, string> = {
  video: "video recording",
  screenshots: "screenshot batch",
  live_scan: "live scan",
};

export function ResumeScanBanner({ onResume }: ResumeScanBannerProps) {
  const { userState, dismissResumeBanner, openLiveScan } = useSession();
  const swipeRef = useRef<SwipeDismissibleHandle>(null);
  const queue = userState?.scanQueue ?? [];
  if (queue.length === 0) return null;

  // Show the most-recent in-flight scan. If a user somehow has two open,
  // they can dismiss this one to surface the next.
  const session = queue[0];
  const label = SOURCE_LABEL[session.source] ?? session.source;
  const progressLabel = session.frameTotal && session.frameTotal > 0
    ? `Frame ${session.frameIndex.toLocaleString()} of ${session.frameTotal.toLocaleString()}`
    : `${session.frameIndex.toLocaleString()} frames in`;

  async function applyDismiss() {
    // Best-effort server cancel so the row never reappears on next boot.
    // UI dismissal happens first so the user sees an immediate response.
    dismissResumeBanner(session.id);
    try {
      await apiRequestRaw("POST", `/api/user/scan-sessions/${session.id}/cancel`);
    } catch {
      // Non-fatal; the row will simply still be in_progress until the
      // next user action closes it. Logged in the console.
    }
  }

  function handleDismissButton() {
    // Run the swipe-out animation when the user taps the X so the visual
    // language matches the gesture-driven path.
    if (swipeRef.current) swipeRef.current.dismiss();
    else applyDismiss();
  }

  function handleResume() {
    if (onResume) {
      onResume(session.source);
      return;
    }
    // Default: open the live scan modal. It covers all three source types
    // well enough for the v1 banner; the orchestrator picks up from the
    // saved frame_index once the user re-engages.
    openLiveScan();
  }

  return (
    <SwipeDismissible
      ref={swipeRef}
      onDismiss={applyDismiss}
      ariaLabel="Resume scan banner. Swipe up to dismiss."
    >
    <section
      data-testid="resume-scan-banner"
      className="rounded-2xl p-4 flex items-start gap-3"
      style={{
        background: "linear-gradient(135deg, hsl(354 78% 30% / 0.45), var(--surface-elev-1) 70%)",
        border: "1px solid hsl(354 78% 56% / 0.4)",
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{
          background: "hsl(354 78% 56% / 0.18)",
          border: "1px solid hsl(354 78% 56% / 0.4)",
          color: "hsl(354 78% 70%)",
        }}
      >
        <Radio className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "hsl(354 78% 70%)" }}>
          Pick up where you left off
        </p>
        <p className="text-sm font-semibold mt-0.5">
          {`Your ${label} is still in progress.`}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {progressLabel}
          {session.caught > 0 || session.seen > 0
            ? `, ${session.caught} caught, ${session.seen} seen so far`
            : ""}
          .
        </p>
        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            data-testid="button-resume-scan"
            onClick={handleResume}
            className="h-8 rounded-full text-xs px-4"
          >
            Resume scan
          </Button>
          <button
            type="button"
            onClick={handleDismissButton}
            data-testid="button-dismiss-resume-scan"
            data-swipe-ignore
            aria-label="Dismiss resume scan banner"
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Not now
          </button>
        </div>
      </div>
    </section>
    </SwipeDismissible>
  );
}
